import { useMemo, useState } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import {
  ClientDetailResponse,
  ContactDetailType,
  EmailTemplateResponse,
  caseNotesApi,
  clientsApi,
  emailTemplatesApi,
} from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { EmailPattern } from "@/common/helper/validationPatterns";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { buildMailtoUrl } from "../EmailSend.utils";

interface EmailSendFormValues {
  recipientEmail: string;
  subject: string;
  body: string;
}

export function useEmailSend(client: ClientDetailResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateResponse | null>(null);

  const { data: templates = [] } = useQuery({
    queryKey: [QueryKey.EmailTemplates],
    queryFn: () => emailTemplatesApi.getAllEmailTemplates(),
  });

  const { data: emailOptions = [] } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, client.clientId],
    queryFn: () => clientsApi.getClientPersonalData({ clientId: client.clientId }),
    select: (data) =>
      data.contactDetails
        .filter((cd) => cd.type === ContactDetailType.Email && cd.value.trim() !== "")
        .map((cd) => cd.value),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        recipientEmail: Yup.string()
          .required(
            t("common.validation.fieldRequired", {
              fieldName: t("clients.client.email.dialog.recipientEmail"),
            })
          )
          .matches(EmailPattern, t("clients.create.invalidEmail")),
      }),
    [t]
  );

  // The case note (Aktenvermerk) is written before the mail program opens, so every opened e-mail is
  // documented; when writing it fails, the mail is not opened either.
  const createCaseNoteFromEmailMutation = useMutation({
    mutationFn: (values: EmailSendFormValues) =>
      caseNotesApi.createCaseNoteFromEmail(
        {
          clientId: client.clientId,
          createCaseNoteFromEmailRequest: {
            recipientEmail: values.recipientEmail,
            subject: values.subject,
            body: values.body,
            originTimeZoneId: getLocalTimeZoneId(),
          },
        },
        withLocalErrorHandling
      ),
    onSuccess: (_data, values) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      // location.assign on a mailto: URL invokes the OS mail client without unloading the SPA.
      window.location.assign(
        buildMailtoUrl({
          recipient: values.recipientEmail,
          subject: values.subject,
          body: values.body,
        })
      );
      onClose();
    },
    onError: () => {
      showError(t("clients.client.email.dialog.caseNoteError"));
    },
  });

  const formik = useFormikWithDirtyForm<EmailSendFormValues>({
    initialValues: {
      recipientEmail: emailOptions[0] ?? "",
      subject: "",
      body: "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    // Awaited so the submit button stays disabled until the note is saved — a second click would
    // otherwise write a second note and open a second mail.
    onSubmit: async (values) => {
      await createCaseNoteFromEmailMutation.mutateAsync(values);
    },
  });

  const { mutate: resolveTemplate } = useMutation({
    mutationFn: (template: EmailTemplateResponse) =>
      emailTemplatesApi.resolveEmailTemplate(
        {
          id: template.id,
          resolveEmailTemplateRequest: { clientId: client.clientId },
        },
        // The no-active-upload-link 404 is an expected flow; onError toasts for every failure.
        withLocalErrorHandling
      ),
    onSuccess: (response, resolvedTemplate) => {
      // useMutation-level onSuccess also fires for every mutate call — ignore late results of a
      // previously selected template so they can't clobber the current selection's content.
      if (resolvedTemplate.id !== selectedTemplate?.id) {
        return;
      }

      // Applied as a new clean baseline, so picking a template cannot trip the unsaved-changes guard.
      formik.resetForm({
        values: { ...formik.values, subject: response.subject, body: response.content },
      });
    },
    onError: async (error, failedTemplate) => {
      if (failedTemplate.id !== selectedTemplate?.id) {
        return;
      }

      if ((await getApiErrorCode(error)) === ApiErrorCode.UploadTokenNotFound) {
        setSelectedTemplate(null);
        showError(t("clients.client.templates.uploadLinkRequired"));
      } else {
        showError(t("clients.client.email.dialog.resolveError"));
      }
    },
  });

  const handleSelectTemplate = (template: EmailTemplateResponse | null) => {
    setSelectedTemplate(template);
    if (template) {
      resolveTemplate(template);
    }
  };

  return { formik, emailOptions, templates, selectedTemplate, handleSelectTemplate };
}
